// Copyright (c) 2025 Juan Beltrán
// Licensed under the MIT License. See LICENSE file for details.

import type { APIConfig, Message, ChatContext } from '../../types/models';
import type { TokenUsage } from '../../types/usage';
import { formatSystemMessages } from '../messageFormatters';
import { usageService } from '../../services/UsageService';
import { getProviderConfig, getProviderUrl, getProviderEndpoint, getModelInfo } from '../../config/modelsDatabase';

function isSpecialModel(modelId: string): boolean {
  const modelLower = modelId.toLowerCase();
  return (
    modelLower.startsWith('o1') ||
    modelLower.startsWith('o3') ||
    modelLower.includes('gpt-4o')
  );
}

function getModelConfig(modelId: string) {
  const model = getModelInfo(modelId);
  if (!model) {
    throw new Error(`Invalid model ID: ${modelId}`);
  }

  const modelLower = modelId.toLowerCase();
  
  // Base configuration
  const config = {};

  // O1/O3 models use max_completion_tokens and store
  if (modelLower.startsWith('o1') || modelLower.startsWith('o3')) {
    return {
      max_completion_tokens: model.maxOutputTokens || model.maxTokens || 1024,
      store: true
    };
  }

  // GPT-4 Optimized models use max_tokens and temperature
  if (modelLower.includes('gpt-4o')) {
    return {
      max_tokens: model.maxOutputTokens || model.maxTokens || 2048,
      temperature: 0.7,
      top_p: 0.9
    };
  }

  // Default configuration for standard models
  return {
    temperature: 0.7,
    max_tokens: model.maxOutputTokens || model.maxTokens || 1024
  };
}

export async function sendOpenAIMessage(
  message: string | { type: 'text' | 'image_url'; text?: string; image_url?: { url: string } }[],
  config: APIConfig,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[],
  userId?: string,
  topUpValue: number = 20
): Promise<string> {
  // Get OpenAI configuration
  const providerConfig = getProviderConfig('openai');
  if (!providerConfig) {
    throw new Error('OpenAI provider configuration not found');
  }

  const chatEndpoint = getProviderEndpoint('openai', 'chat');
  if (!chatEndpoint) {
    throw new Error('OpenAI chat endpoint not found');
  }

  const endpoint = config.endpoint || getProviderUrl('openai', chatEndpoint);
  const isSpecial = isSpecialModel(modelId);
  
  // Format messages based on model type and content
  const formatContent = (content: string | Array<{type: string, text?: string, image_url?: {url: string}}>) => {
    if (typeof content === 'string') {
      // If it's a base64 image data URL
      if (content.startsWith('data:image')) {
        return [
          {
            type: "text",
            text: "What is in this image?"
          },
          {
            type: "image_url",
            image_url: {
              url: content
            }
          }
        ];
      }
      return content;
    }
    return content;
  };

  const messages = isSpecial ? [
    { role: "user", content: "You are a helpful assistant." },
    ...previousMessages.map(msg => ({
      role: "user",
      content: formatContent(msg.content)
    })),
    { role: "user", content: formatContent(message) }
  ] : [
    ...formatSystemMessages(context),
    ...previousMessages.map(msg => ({
      role: msg.role,
      content: formatContent(msg.content)
    })),
    { role: 'user', content: formatContent(message) }
  ];

  const modelConfig = getModelConfig(modelId);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`,
      ...providerConfig.headers
    },
    body: JSON.stringify({
      model: modelId,
      messages,
      ...modelConfig
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `OpenAI API Error: ${response.statusText}`);
  }

  const data = await response.json();
  if (!data.choices?.[0]?.message?.content) {
    throw new Error('Invalid response from OpenAI API');
  }

  // Record usage if available in the response
  console.log('OpenAI API Response:', data);
  if (data.usage && userId) {
    const usage: TokenUsage = {
      prompt_tokens: data.usage.prompt_tokens,
      completion_tokens: data.usage.completion_tokens,
      total_tokens: data.usage.total_tokens
    };

    console.log('Recording OpenAI usage:', {
      userId,
      modelId,
      usage,
      topUpValue
    });

    try {
      await usageService.recordUsage(userId, modelId, usage, topUpValue);
    } catch (error) {
      console.error('Error recording usage:', error);
      // Don't throw here - we still want to return the response even if usage recording fails
    }
  } else {
    console.warn('No usage data or user ID available:', {
      hasUsage: !!data.usage,
      hasUserId: !!userId
    });
  }

  return data.choices[0].message.content;
}